import {store} from '../../store';
import {Carts, updateCartPackageShippingMethod} from '../../reducers/cartReducer';
import {startModalLoading, stopModalLoading} from '../../reducers/environmentReducer';
import {type ICartCalculationRecord, type ICartMetaData, type IShippingMethod, type IShippingPackage} from '../../../../@type/woocommerce/cart-calculation';
import {formatCurrencyString} from '../../util/currency';
import {requestCartCalculation} from '../../payment/order';
import {buildSubscriptionPriceMetaData} from '../../util/subscription';
import {getLocaleText} from '../../util/translation';
import {$qs, $qsAll} from '../../../../@shared/ts/dom';
import {SDKFlags} from '../../sdk';
import {PeachPayCustomer} from '../../reducers/peachPayCustomerReducer';

function initShippingOptionsFormEvents() {
	/**
     * Handle shipping option selection.
     */
	$qs('#pp-shipping-options')?.addEventListener('change', async (e: Event) => {
		const $target = e.target as HTMLInputElement;
		const $targetContainer = $target.closest<HTMLElement>('[data-cart-key]');

		const shippingMethodId = $target.value ?? '';
		const cartKey = $targetContainer?.dataset?.['cartKey'] ?? '';
		const packageKey = $targetContainer?.dataset?.['packageKey'] ?? '';

		store.dispatch(updateCartPackageShippingMethod({
			cartKey,
			shippingPackageKey: packageKey,
			packageMethodId: shippingMethodId,
		}));

		// Update the tax and other values that may change with different shipping cost.
		store.dispatch(startModalLoading());

		await requestCartCalculation('push', 'shipping-method:change');

		SDKFlags.setRefresh();

		store.dispatch(stopModalLoading());
	});

	/**
     * Handle rerendering the shipping options when the cart changes.
     */
	store.subscribe(() => {
		$qsAll('.hide-for-virtual-carts').forEach($element => {
			$element.classList.toggle('hide', !Carts.needsShipping());
		});

		$qsAll('.show-for-virtual-carts').forEach($element => {
			$element.classList.toggle('hide', Carts.needsShipping());
		});

		if (Carts.needsShipping()) {
			renderCartShippingOptions(store.getState().calculatedCarts);

			$qs('#pp-shipping-address-error', $element => {
				$element.classList.toggle('hide', Carts.anyShippingMethodsAvailable());

				if (Carts.anyShippingMethodsAvailable()) {
					return;
				}

				const formattedAddress = PeachPayCustomer.shipToDifferentAddress()
					? PeachPayCustomer.shipping.formattedAddress()
					: PeachPayCustomer.billing.formattedAddress();

				$element.innerHTML = `${getLocaleText('No shipping options were found for')} <strong>${formattedAddress.join(', ')}</strong>. ${getLocaleText('Please ensure that your address has been entered correctly, choose a different shipping address, or contact us if you need any help.')}`;
			});
		}
	});
}

/**
 * Renders all possible shipping options based on all calculated carts
 */
function renderCartShippingOptions(calculatedCarts: ICartCalculationRecord) {
	let shippingOptionsHTML = '';
	const cartCalculationCount = Object.entries(calculatedCarts).length;
	for (const [cartKey, cartCalculation] of Object.entries(calculatedCarts)) {
		if (!cartCalculation) {
			continue;
		}

		for (const [shippingPackageKey, shippingPackage] of Object.entries(cartCalculation.package_record)) {
			if (!shippingPackage) {
				continue;
			}

			shippingOptionsHTML += renderShippingPackageOptions(cartKey, shippingPackageKey, shippingPackage, cartCalculation.cart_meta, cartCalculationCount > 1);
		}
	}

	$qs('#pp-shipping-options', $element => {
		$element.innerHTML = shippingOptionsHTML;
	});
}

function escapeHTML(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
}

function shouldRenderPackageName(packageName: string, shouldShowPackageName: boolean): boolean {
	if (shouldShowPackageName) {
		return true;
	}

	return packageName.trim() !== '' && packageName !== 'Shipping';
}

/**
 * Builds the HTML needed for a single package shipping options.
 */
// eslint-disable-next-line max-params
function renderShippingPackageOptions(cartKey: string, shippingPackageKey: string, shippingPackage: IShippingPackage, cartMeta: ICartMetaData, shouldShowPackageName: boolean): string {
	const methodOptionBuilder = (methodKey: string, method: IShippingMethod, selected: boolean) => `
<div class="pp-disabled-loading pp-radio-line pp-shipping-option-row${selected ? ' fill' : ''}" for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
	<div class="pp-shipping-option-bg"></div>
	<input class="pp-disabled-loading" id="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}" name="shipping_method[${shippingPackageKey}]" value="${methodKey}" type="radio" ${
	selected ? 'checked' : ''
} required>
	${
	method.description
		? `<div class="flex col w-100">
				<label for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
					<span>${method.title}</span>
					<span class="shipping-price pp-currency-blur">
						${formatCurrencyString(method.total)}
						<span class="muted">${buildSubscriptionPriceMetaData(cartMeta)}</span>
					</span>
				</label>
			<div>${method.description}</div>
		</div>`
		: `<label style="width: 100%;" for="shipping_method_${shippingPackageKey}_${methodKey.replace(/:/g, '')}">
				<span>${method.title}</span> <span class="shipping-price pp-currency-blur">${formatCurrencyString(method.total)}
				<span class="muted">${buildSubscriptionPriceMetaData(cartMeta)}</span></span>
				</label>`
}
</div>`;

	/**
	 * Since the package name string is being displayed in the modal but coming from Woocommerce, we need to hardcode these strings' values
	 * so they can be captured by the gettext parser and be given translations.
	 */
	const packageNameTranslations: Record<string, string> = {
		Shipping: getLocaleText('Shipping'),
		'Initial Shipment': getLocaleText('Initial Shipment'),
		'Recurring Shipment': getLocaleText('Recurring Shipment'),
	};
	const packageName = packageNameTranslations[shippingPackage.package_name] ?? shippingPackage.package_name;
	const packageNameHTML = `<div class="pp-title">${escapeHTML(packageName)}</div>`;
	const packageMethodOptionsHTML: string = Object.entries(shippingPackage.methods).map(([shippingMethodKey, shippingMethod]) =>
		shippingMethod ? methodOptionBuilder(shippingMethodKey, shippingMethod, shippingPackage.selected_method === shippingMethodKey) : '',
	).join('');

	return `${shouldRenderPackageName(shippingPackage.package_name, shouldShowPackageName) ? packageNameHTML : ''}
	<div class="pp-shipping-options-container" data-cart-key="${cartKey}" data-package-key="${shippingPackageKey}">
	${packageMethodOptionsHTML}
	</div>`;
}

export {
	initShippingOptionsFormEvents,
	renderCartShippingOptions,
};
